import { PRODUCTION_API_URL } from '@/lib/apiConfig';
import { isNativeApp, nativeRequestHeaders } from './native';

const TENANT_ID = import.meta.env.VITE_AZURE_TENANT_ID || '';
const CLIENT_ID = import.meta.env.VITE_AZURE_CLIENT_ID || '';
// Retain the callback used by the existing native Microsoft registration.
// Both hosts serve production; API routing must not silently change Azure redirects.
const REDIRECT_URI = 'https://sep.seikosportstech.co.uk/auth/mobile-callback';
const SCOPES = 'openid profile User.Read';

let pendingAuth = null;
let browserFinishedListener = null;

function base64UrlEncode(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function generateCodeVerifier() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

async function generateCodeChallenge(verifier) {
  const data = new TextEncoder().encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(hash);
}

export async function startNativeLogin() {
  const { Browser } = await import('@capacitor/browser');

  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = base64UrlEncode(crypto.getRandomValues(new Uint8Array(16)));

  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    response_type: 'code',
    redirect_uri: REDIRECT_URI,
    scope: SCOPES,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    state,
  });

  const authUrl = `https://login.microsoftonline.com/${TENANT_ID}/oauth2/v2.0/authorize?${params}`;

  const promise = new Promise((resolve, reject) => {
    pendingAuth = { resolve, reject, codeVerifier, state };
  });

  if (browserFinishedListener) {
    browserFinishedListener.then(h => h.remove()).catch(() => {});
  }
  browserFinishedListener = Browser.addListener('browserFinished', () => {
    browserFinishedListener.then(h => h.remove()).catch(() => {});
    browserFinishedListener = null;
    if (pendingAuth) {
      pendingAuth.reject(new Error('cancelled'));
      pendingAuth = null;
    }
  });

  await Browser.open({ url: authUrl, presentationStyle: 'popover' });
  return promise;
}

export async function handleAuthCallback(url) {
  if (!pendingAuth) return false;

  const parsed = new URL(url);
  const code = parsed.searchParams.get('code');
  const state = parsed.searchParams.get('state');
  const error = parsed.searchParams.get('error');

  if (browserFinishedListener) {
    browserFinishedListener.then(h => h.remove()).catch(() => {});
    browserFinishedListener = null;
  }

  if (error) {
    pendingAuth.reject(new Error(parsed.searchParams.get('error_description') || error));
    pendingAuth = null;
    return true;
  }

  if (!code || state !== pendingAuth.state) {
    pendingAuth.reject(new Error('Invalid auth callback'));
    pendingAuth = null;
    return true;
  }

  try {
    const { Browser } = await import('@capacitor/browser');
    await Browser.close();
  } catch {}

  const { codeVerifier, resolve, reject } = pendingAuth;
  pendingAuth = null;

  try {
        const res = await fetch(`${PRODUCTION_API_URL}/auth/exchange-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...nativeRequestHeaders() },
      body: JSON.stringify({
        code,
        code_verifier: codeVerifier,
        redirect_uri: REDIRECT_URI,
      }),
    });

    const data = await res.json();
    if (!res.ok) {
      console.error('Exchange code error:', data);
      throw new Error(data.error || 'Login failed');
    }
    resolve(data.token);
  } catch (err) {
    reject(err);
  }

  return true;
}

export function setupNativeAuthListener() {
  if (!isNativeApp()) return;
  import('@capacitor/app').then(({ App }) => {
    App.addListener('appUrlOpen', ({ url }) => {
      if (url.includes('auth/callback')) {
        handleAuthCallback(url);
      }
    });
  });
}
